import { loadImage } from "client/utils/imageEdit/loadImage";
import { useContext, useEffect, useState } from "react";
import { validateResizeDimension } from "client/utils/validateResizeDimension";
import { setImage } from "store/features/enlarge/actions";
import { enlargeSelector } from "store/features/enlarge/selectors/enlargeSelector";
import { useAppDispatch, useAppSelector } from "store/store";
import { MAX_RESIZE_SIZE_IN_PX } from "./Preview";
import { downloadImageInFrontendTools } from "client/utils/downloadImageInFrontendTools";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useRouter } from "next/router";
import { useToast } from "components/common/Toast/useToast";
import { translateFunc } from "client/utils/translateFunc";
import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { loadSrcToImage } from "client/utils/loadSrcToImage";
import { formatBytes } from "@/utils/formatBytes";
import { Size } from "@/store/types/previewImage";
import { useValidateMaxFileSize } from "@/modules/DailyQuota/hooks/useValidateMaxFileSize";
import translatedRoutesSlugs from "translatedRouteSlugs";

export const usePreview = () => {
    const {
        image,
        resizeSettings: { unit, dimensionType, byPercentage },
        upload,
    } = useAppSelector(enlargeSelector);
    const { initialImageRef } = useContext(CanvasContext);
    const dimensionsValid = validateResizeDimension(
        image,
        dimensionType,
        unit,
        byPercentage.percentage,
        MAX_RESIZE_SIZE_IN_PX,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [downloaded, setDownloaded] = useState<Size>("0 KB");
    const dispatch = useAppDispatch();
    const [src, setSrc] = useState("");
    const router = useRouter();
    const toast = useToast();
    const errorFromEditorText = translateFunc(`common:corrupt-image-toast-test`);
    const { validateMaxFileSize } = useValidateMaxFileSize();
    const pricingPageLink =
        router.locale === "en"
            ? "/pricing"
            : `/${router.locale}/${translatedRoutesSlugs["/pricing"][router.locale || "en"]}`;

    const handleMaxFileSizeValidationFailed = () => {
        window.location.replace(`${pricingPageLink}?maxFileSizeError=true`);
    };

    const handleLoadImage = async () => {
        if (!initialImageRef?.current) return;
        const { src } = await downloadImageInFrontendTools(
            upload,
            image.src,
            0,
            async (_progress, downloaded) => {
                if (!(await validateMaxFileSize([downloaded]))) {
                    handleMaxFileSizeValidationFailed();
                    return;
                }
                setDownloaded(formatBytes(downloaded));
            },
            {},
            image.format,
        );
        setSrc(src);
        const { image: img, error } = await loadImage(src);

        if (error) {
            toast.toast({
                closeable: true,
                position: "top-right",
                status: "error",
                text: errorFromEditorText,
                time: DEFAULT_TOAST_SHOW_TIME,
            });
            setTimeout(() => {
                setIsLoading(false);
                router.back();
            }, 500);
        } else {
            await loadSrcToImage(src, initialImageRef.current);
            setIsLoading(false);

            dispatch(
                setImage({
                    createdAt: new Date(),
                    format: image.format,
                    original: {
                        width: img.width,
                        height: img.height,
                        size: image.original.size,
                    },
                    preview: {
                        width: img.width,
                        height: img.height,
                        size: image.preview.size,
                    },
                    src: image.src,
                    title: image.title,
                }),
            );
        }
    };

    useEffect(() => {
        image.src && handleLoadImage();
    }, [image.src]);

    return { image, unit, dimensionsValid, isLoading, src, initialImageRef, downloaded };
};
